/**
 * POST-NATIONGATE ACTIVATION™
 *
 * Drives the post-registration sequence:
 *   Registro → Nación → Predicción → Share → Creator Studio
 *
 * Pure listener — does not touch Prediction Engine, Share Engine,
 * Nation Wars scoring, API Football, Viral Cards or Open Graph.
 * Subscribes to the global `vs:event` window event dispatched from
 * `trackEvent` and persists progress in localStorage.
 */

export type ActivationStep = "nation" | "prediction" | "share" | "creator" | "done";

const KEYS = {
  nation: "vs:activation:nation",
  prediction: "vs:activation:prediction",
  share: "vs:activation:share",
  creator: "vs:activation:creator",
} as const;

const EVENT_NAME = "vs:activation:changed";

function safeGet(key: string): string | null {
  try { return window.localStorage.getItem(key); } catch { return null; }
}
function safeSet(key: string, value: string) {
  try { window.localStorage.setItem(key, value); } catch { /* noop */ }
}

export function getActivationStep(): ActivationStep {
  if (typeof window === "undefined") return "nation";
  if (!safeGet(KEYS.nation)) return "nation";
  if (!safeGet(KEYS.prediction)) return "prediction";
  if (!safeGet(KEYS.share)) return "share";
  if (!safeGet(KEYS.creator)) return "creator";
  return "done";
}

function mark(step: keyof typeof KEYS) {
  if (typeof window === "undefined") return;
  if (safeGet(KEYS[step])) return;
  safeSet(KEYS[step], new Date().toISOString());
  try { window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: { step } })); } catch { /* noop */ }
}

export function markNationSelected() { mark("nation"); }
export function markPredictionMade() { mark("prediction"); }
export function markShareMade() { mark("share"); }
export function markCreatorEntered() { mark("creator"); }

export function subscribeActivation(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = () => listener();
  window.addEventListener(EVENT_NAME, handler);
  window.addEventListener("storage", handler);
  return () => {
    window.removeEventListener(EVENT_NAME, handler);
    window.removeEventListener("storage", handler);
  };
}

/** Bootstraps the global event listener. Call once at app start. */
let bootstrapped = false;
export function bootstrapActivationListener() {
  if (bootstrapped || typeof window === "undefined") return;
  bootstrapped = true;
  // P0-WOW-02: measure prediction → share latency. Started by
  // `prediction_wow_shown`, ended by the first share success event.
  let wowShownAt: number | null = null;
  let latencyEmitted = false;
  window.addEventListener("vs:event", (ev: Event) => {
    const detail = (ev as CustomEvent<{ event_type?: string; event_data?: Record<string, unknown> }>).detail ?? {};
    const type = String(detail.event_type ?? "");
    if (!type) return;
    if (type === "nation_selected") markNationSelected();
    else if (
      type === "prediction_vote" ||
      type === "prediction_answered" ||
      type === "prediction_submitted" ||
      type === "bracket_prediction_submitted"
    ) markPredictionMade();
    else if (
      type.startsWith("nation_wars_share_") ||
      type === "share_native_completed" ||
      type === "share_whatsapp_clicked" ||
      type === "share_copy_completed" ||
      type === "auto_viral_capsule_shared"
    ) {
      markShareMade();
      if (wowShownAt && !latencyEmitted) {
        latencyEmitted = true;
        const seconds = Math.round((Date.now() - wowShownAt) / 1000);
        void import("@/lib/audit").then(({ trackEvent }) => {
          void trackEvent("prediction_to_share_seconds", { seconds, share_event: type });
        }).catch(() => { /* noop */ });
      }
    }
    else if (type === "prediction_wow_shown") {
      wowShownAt = Date.now();
      latencyEmitted = false;
    }
  });
}